// `injected` still fails loud for the optional members this package keeps.
// `useIsAuthenticated` reads `@heidi/auth/state`; the app wires that seam once.

import { useIsAuthenticated as useAuthIdentity } from '@heidi/auth/state';

import { getAgentsDataConfig, type AgentsDataConfig } from './config';

/**
 * Fails loudly and BY NAME. `getAgentsDataConfig` only knows whether the seam
 * was configured at all, so an un-injected member would otherwise reach a
 * consumer as `undefined` and blow up somewhere with no member in the message.
 *
 * Exported for the package's other readers of an optional member, such as
 * `./injected-identity`, so they all fail with this one message.
 * NOT on the `./data` door: `data/index.ts` names what this module publishes,
 * so a package-internal export stops here.
 */
export const injected = <K extends keyof AgentsDataConfig>(
  member: K
): NonNullable<AgentsDataConfig[K]> => {
  const value = getAgentsDataConfig()[member];
  if (value === undefined) {
    throw new Error(
      `[agents/data] \`${member}\` is not configured — the app injects it at ` +
        'src/composition/hosts/agents/agents-data-wiring.ts (ruling 65); a test exercising this ' +
        'reader must configure a double for that member first.'
    );
  }
  return value as NonNullable<AgentsDataConfig[K]>;
};

/** The provider-aware authentication verdict, from the auth identity hook. */
export const useIsAuthenticated = (): boolean => useAuthIdentity().isAuthenticated;
